import { useAuthentication } from '@features/auth/context'
import { ChevronLeft, ChevronRight, CircleCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RentModel } from 'types/api'

import { cn } from '@/lib/utils'

import AllRentsPaid from './AllRentsPaid'
import { EditRentFormModal } from './RentEdit'
import RentPayment from './RentPayment'

export interface RentWidgetProps {
  rents: RentModel[]
}

export function UnpaidRentWidget({ rents }: RentWidgetProps) {
  const unpaidRents = rents?.filter((rent) => !rent.paid)
  if (unpaidRents?.length === 0) {
    return <AllRentsPaid />
  }

  return (
    <div className="space-y-4">
      {unpaidRents?.map((rent) => (
        <RentPayment key={rent.id} rent={rent} />
      ))}
    </div>
  )
}

interface PaidRentWidgetProps {
  paidRents: RentModel[]
}

const PAGE_SIZE = 6

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('sk-SK') : '-'

const TH = 'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-4 align-middle text-sm text-ink'

export function PaidRentWidget({ paidRents }: PaidRentWidgetProps) {
  const { t } = useTranslation('contract')
  const { role } = useAuthentication()
  const isAdmin = role === 'ADMIN'
  const [page, setPage] = useState(0)

  const pages = Math.ceil(paidRents.length / PAGE_SIZE)
  const rows = paidRents.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  const statusPill = (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
      <CircleCheck className="h-3.5 w-3.5" />
      {t('rentWidget.paidTab', { ns: 'offers' })}
    </span>
  )

  return (
    <div>
      {/* Mobile: stacked rows */}
      <div className="divide-y divide-ink/[0.06] sm:hidden">
        {rows.map((rent) => (
          <div key={rent.id} className="space-y-2.5 px-4 py-4">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink">
                {t(`rent_table.rents.${rent.payment_type}`)}
              </span>
              {statusPill}
            </div>
            <div className="font-display text-xl font-bold leading-none text-ink">
              {Number(rent.amount).toFixed(2)}€
            </div>
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">
                  {t('rent_table.rents.due_date')}
                </span>
                <span className="font-medium text-ink">
                  {formatDate(rent.due_date)}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">
                  {t('rent_table.rents.paid_date')}
                </span>
                <span className="font-medium text-ink">
                  {formatDate(rent.paid_date)}
                </span>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <span
                  title={rent.description ?? ''}
                  className="min-w-0 truncate text-sm text-muted-foreground"
                >
                  {rent.description || '-'}
                </span>
                {rent.id && (
                  <EditRentFormModal rentId={rent.id} initialValues={rent} />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
              <th className={TH}>{t('rent_table.rents.due_date')}</th>
              <th className={TH}>{t('rent_table.rents.paid_date')}</th>
              <th className={TH}>{t('rent_table.rents.type')}</th>
              <th className={TH}>{t('rent_table.rents.amount')}</th>
              <th className={TH}>{t('rent_table.rents.status')}</th>
              {isAdmin && (
                <>
                  <th className={TH}>{t('rent_table.rents.description')}</th>
                  <th className={cn(TH, 'text-right')}>
                    {t('rent_table.actions')}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((rent) => (
              <tr
                key={rent.id}
                className="border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
              >
                <td className={cn(TD, 'whitespace-nowrap')}>
                  {formatDate(rent.due_date)}
                </td>
                <td className={cn(TD, 'whitespace-nowrap')}>
                  {formatDate(rent.paid_date)}
                </td>
                <td className={TD}>
                  {t(`rent_table.rents.${rent.payment_type}`)}
                </td>
                <td className={cn(TD, 'font-semibold')}>
                  {Number(rent.amount).toFixed(2)}€
                </td>
                <td className={TD}>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
                    <CircleCheck className="h-3.5 w-3.5" />
                    {t('rentWidget.paidTab', { ns: 'offers' })}
                  </span>
                </td>
                {isAdmin && (
                  <>
                    <td className={cn(TD, 'text-muted-foreground')}>
                      <span
                        title={rent.description ?? ''}
                        className="block max-w-[180px] truncate"
                      >
                        {rent.description || '-'}
                      </span>
                    </td>
                    <td className={cn(TD, 'text-right')}>
                      {rent.id && (
                        <EditRentFormModal
                          rentId={rent.id}
                          initialValues={rent}
                        />
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-1.5 px-4 py-3">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: pages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setPage(i)}
              className={cn(
                'flex h-8 min-w-8 items-center justify-center rounded-full border-0 px-2.5 text-sm font-semibold transition-colors',
                i === page
                  ? 'bg-primary text-white'
                  : 'bg-transparent text-muted-foreground hover:bg-ink/[0.05]'
              )}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            disabled={page === pages - 1}
            onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
